/**
 * Lobby background music player.
 *
 * A lightweight singleton that cycles through the ambient score in a fixed
 * order, loops back to the top, and crossfades between adjacent tracks so
 * the lobby never gets a hard cut. It is intentionally decoupled from
 * React: the player owns its own HTMLAudioElements (two, for seamless
 * crossfades) and survives route changes. A small React controller
 * (`MusicController.tsx`) boots the singleton, wires the mute toggle, and
 * handles the browser's "audio needs a user gesture" policy.
 *
 * Design notes:
 * - We favour `<audio>` elements over `AudioContext` + `decodeAudioData`
 *   because streaming 2–3 MB MP3s avoids a multi-megabyte decode burst
 *   and memory stays bounded. Element `.volume` is all we need for a
 *   nice fade.
 * - Playlist order matches the user-provided spec:
 *     1. A New Dawn           → 9. Midnight in a Silent World   (loops)
 * - Crossfade window: last `FADE_SECONDS` of the current track overlaps
 *   with the first `FADE_SECONDS` of the next, so both tracks play
 *   simultaneously during the fade.
 */

import { isMusicMuted } from '@/lib/sounds';

export interface MusicTrack {
  title: string;
  src: string;
}

// Ordered playlist. Filenames match `public/music/` — the loader ship
// number prefix exists so both humans and `Array.sort()` keep the order
// intact even if someone imports this list somewhere unusual.
export const MUSIC_PLAYLIST: MusicTrack[] = [
  { title: 'A New Dawn',                  src: '/music/01-a-new-dawn.mp3' },
  { title: 'Sunlit Meadow',               src: '/music/02-sunlit-meadow.mp3' },
  { title: 'Liminal Memory Loop',         src: '/music/03-liminal-memory-loop.mp3' },
  { title: 'Wary Engagement',             src: '/music/04-wary-engagement.mp3' },
  { title: 'Unsheathed Steel',            src: '/music/05-unsheathed-steel.mp3' },
  { title: 'The Fading Kingdom',          src: '/music/06-the-fading-kingdom.mp3' },
  { title: 'The Settling Fields',         src: '/music/07-the-settling-fields.mp3' },
  { title: 'Whispers of a Forgotten Realm', src: '/music/08-whispers-of-a-forgotten-realm.mp3' },
  { title: 'Midnight in a Silent World',  src: '/music/09-midnight-in-a-silent-world.mp3' },
];

// Playback tuning
const TARGET_VOLUME = 0.45;        // Never louder than this — this is background
const FADE_SECONDS = 3.5;          // Crossfade duration between tracks
const TICK_MS = 80;                // How often to poll currentTime / fade ramps

type PlayerEventName = 'track-changed' | 'state-changed' | 'playlist-changed';

interface PlayerState {
  /** Whether the player has been booted (user gesture consumed). */
  ready: boolean;
  /** Whether audio is actively playing (not just mounted). */
  playing: boolean;
  /** Index into MUSIC_PLAYLIST of the currently-audible track. */
  currentIndex: number;
}

/** localStorage/Web-Locks key — same origin so every tab contends. */
const LEADER_LOCK_NAME = 'takeover-lobby-music-leader';

/**
 * localStorage key for the user's enabled-track selection. Stored as a
 * JSON array of playlist indices so adding / reordering tracks in the
 * future stays backwards-compatible (unknown indices are ignored on
 * restore, missing ones default to enabled).
 */
const PLAYLIST_ENABLED_KEY = 'takeover-music-enabled-v1';

/** Last audible track + position so a refresh does not rewind to track 1 / t=0. */
const PLAYBACK_CURSOR_KEY = 'takeover-music-playback-v1';

class MusicPlayer {
  private activeEl: HTMLAudioElement | null = null;
  private nextEl: HTMLAudioElement | null = null;

  private currentIndex = 0;
  private crossfadeScheduled = false;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  private fadeEndsAt = 0;
  private ready = false;
  private playing = false;
  private hasLeadership = false;
  private leadershipPromise: Promise<void> | null = null;

  /** Seconds to seek after `start()` when restoring from {@link PLAYBACK_CURSOR_KEY}. */
  private resumeOffsetSec = 0;
  /** Wall-clock time of last cursor write — initialized so the first tick does not spam persist. */
  private lastPlaybackPersistAt = typeof Date !== 'undefined' ? Date.now() : 0;

  /**
   * Set of playlist indices the user wants in the rotation. Persisted to
   * localStorage so the choice survives reloads. Initialised to "every
   * track" so first-time users hear the full score; restored from storage
   * on construction if we're in a browser.
   *
   * Invariant: `enabledSet.size >= 1` at all times. `setTrackEnabled()`
   * refuses to remove the last enabled track so the playlist never
   * empties by accident — an empty playlist is an unpleasant silent
   * failure mode (mute toggle turns on, nothing plays, user confused).
   */
  private enabledSet: Set<number> = new Set(
    MUSIC_PLAYLIST.map((_, i) => i),
  );

  private listeners: Record<PlayerEventName, Set<() => void>> = {
    'track-changed': new Set(),
    'state-changed': new Set(),
    'playlist-changed': new Set(),
  };

  constructor() {
    this.restoreEnabledSet();
    this.restorePlaybackCursor();
    // If the persisted selection doesn't include the default starting
    // index, jump the cursor to the first enabled track so `start()`
    // boots into something the user actually wants to hear.
    if (!this.enabledSet.has(this.currentIndex)) {
      this.currentIndex = this.firstEnabledIndex();
    }
  }

  /**
   * Acquire an exclusive lock across same-origin tabs so only one tab
   * actually drives playback. Without this, opening the app in two tabs
   * would overlap two independent music streams.
   *
   * Uses the Web Locks API (Chrome 69+, Firefox 96+, Safari 15.4+). The
   * callback returns a never-resolving promise so the lock is held for
   * the full tab lifetime — the browser releases it automatically on
   * page unload, at which point a waiting tab is promoted.
   *
   * If `navigator.locks` is unavailable (ancient browsers), we silently
   * fall back to the pre-lock behaviour: leadership is granted
   * immediately and every open tab plays. Acceptable degradation.
   *
   * Safe to call repeatedly — the first call caches the in-flight promise
   * so subsequent callers await the same lock attempt.
   */
  acquireLeadership(): Promise<void> {
    if (this.leadershipPromise) return this.leadershipPromise;
    if (typeof navigator === 'undefined' || !('locks' in navigator) || !navigator.locks) {
      this.hasLeadership = true;
      this.leadershipPromise = Promise.resolve();
      return this.leadershipPromise;
    }
    this.leadershipPromise = new Promise<void>((resolveAcquired) => {
      void navigator.locks.request(
        LEADER_LOCK_NAME,
        { mode: 'exclusive' },
        () => {
          this.hasLeadership = true;
          resolveAcquired();
          return new Promise<void>(() => {});
        },
      );
    });
    return this.leadershipPromise;
  }

  isLeader(): boolean {
    return this.hasLeadership;
  }

  /**
   * Boot the player from a user gesture. Must be called inside a click
   * / key / touch handler or the browser will refuse to start audio.
   * Safe to call more than once — subsequent calls just ensure playback
   * is running in accordance with the current mute state.
   *
   * No-op (other than flipping `ready`) on follower tabs so those never
   * fetch the MP3 playlist or contend for the audio output device.
   */
  async start(): Promise<void> {
    if (typeof window === 'undefined') return;
    if (!this.hasLeadership) {
      if (!this.ready) {
        this.ready = true;
        this.emit('state-changed');
      }
      return;
    }
    if (!this.activeEl) {
      // Respect the user's enabled-tracks selection: if track 0 is
      // disabled, boot into the first enabled track instead so the
      // opening moment of playback isn't something they muted out.
      const bootIndex = this.enabledSet.has(this.currentIndex)
        ? this.currentIndex
        : this.firstEnabledIndex();
      if (bootIndex !== this.currentIndex) {
        this.currentIndex = bootIndex;
        this.emit('track-changed');
      }
      this.activeEl = this.makeElement(MUSIC_PLAYLIST[bootIndex].src);
      this.activeEl.volume = 0; // Fade-in on first start
      this.applyResumeSeekIfNeeded(this.activeEl);
      this.syncActiveLoop();
    }
    if (!this.ready) {
      this.ready = true;
      this.emit('state-changed');
    }
    if (isMusicMuted()) {
      // Leave element mounted / paused — we'll resume on unmute.
      return;
    }
    try {
      await this.activeEl.play();
      this.playing = true;
      this.emit('state-changed');
      this.fadeTo(this.activeEl, TARGET_VOLUME, 1.6);
      this.ensureTick();
    } catch {
      // Autoplay still blocked (rare once a gesture has fired). Leave
      // the element ready so the next user gesture can retry.
      this.playing = false;
      this.emit('state-changed');
    }
  }

  /** Unmute = resume playback; mute = fade out + pause. */
  async applyMute(muted: boolean): Promise<void> {
    if (!this.activeEl) {
      // If we haven't been booted yet, just record state via emit so the
      // UI reflects it. Playback will honour the mute on the next start().
      this.emit('state-changed');
      return;
    }
    if (muted) {
      this.fadeTo(this.activeEl, 0, 0.8, () => {
        if (this.activeEl) this.activeEl.pause();
        if (this.nextEl) {
          this.nextEl.pause();
          this.nextEl.currentTime = 0;
          this.nextEl.volume = 0;
        }
        this.playing = false;
        this.emit('state-changed');
      });
    } else {
      try {
        await this.activeEl.play();
        this.playing = true;
        this.emit('state-changed');
        this.fadeTo(this.activeEl, TARGET_VOLUME, 1.2);
        this.ensureTick();
      } catch {
        // Still blocked (no gesture yet). The controller will retry on
        // the next user gesture.
      }
    }
  }

  getState(): PlayerState {
    return {
      ready: this.ready,
      playing: this.playing,
      currentIndex: this.currentIndex,
    };
  }

  getCurrentTrack(): MusicTrack {
    return MUSIC_PLAYLIST[this.currentIndex];
  }

  subscribe(event: PlayerEventName, fn: () => void): () => void {
    this.listeners[event].add(fn);
    return () => this.listeners[event].delete(fn);
  }

  /**
   * Manually jump to the next enabled track (crossfaded). No-op when the
   * user has muted music — otherwise hitting skip would unexpectedly
   * resume playback on a new track despite the mute toggle being off.
   * The footer UI disables the button while muted for the same reason,
   * but this guard means a stray keyboard shortcut or programmatic call
   * can't sneak audio in either.
   */
  next(): void {
    if (isMusicMuted()) return;
    const target = this.findEnabledNeighbour(this.currentIndex, 'forward');
    if (target === null || target === this.currentIndex) return;
    this.scheduleCrossfade(true, target);
  }

  /**
   * Jump to the previous enabled track. Same mute guard as `next()` —
   * navigation must never spontaneously un-mute playback.
   */
  previous(): void {
    if (isMusicMuted()) return;
    const target = this.findEnabledNeighbour(this.currentIndex, 'backward');
    if (target === null || target === this.currentIndex) return;
    this.scheduleCrossfade(true, target);
  }

  /**
   * Jump to a specific playlist index (e.g. row play button). Enables the
   * track if it was off. When muted, swaps the cursor / active element
   * without audible playback so choosing a track never un-mutes by
   * surprise; when unmuted, crossfades like next/prev.
   */
  playTrackAt(index: number): void {
    if (typeof window === 'undefined') return;
    if (!Number.isInteger(index) || index < 0 || index >= MUSIC_PLAYLIST.length) {
      return;
    }

    if (!this.enabledSet.has(index)) {
      this.enabledSet.add(index);
      this.persistEnabledSet();
      this.emit('playlist-changed');
    }

    // Same index is a no-op only once we have a real element for it.
    // If we're waiting on the Web Lock, we may have already set
    // `currentIndex` on a follower pass — the deferred `playTrackAt` must
    // not return early or audio never starts.
    if (index === this.currentIndex && this.activeEl) {
      return;
    }

    if (!this.hasLeadership) {
      this.currentIndex = index;
      this.emit('track-changed');
      void this.acquireLeadership().then(() => {
        this.playTrackAt(index);
      });
      return;
    }

    if (!this.ready) {
      this.ready = true;
      this.emit('state-changed');
    }

    this.resumeOffsetSec = 0;

    if (isMusicMuted()) {
      this.jumpSilent(index);
      this.syncActiveLoop();
      return;
    }

    if (!this.activeEl) {
      this.currentIndex = index;
      this.activeEl = this.makeElement(MUSIC_PLAYLIST[index].src);
      this.activeEl.volume = 0;
      this.syncActiveLoop();
      this.emit('track-changed');
      void this.activeEl
        .play()
        .then(() => {
          if (this.activeEl && !this.activeEl.paused) {
            this.playing = true;
            this.fadeTo(this.activeEl, TARGET_VOLUME, 1.6);
            this.ensureTick();
          }
          this.emit('state-changed');
        })
        .catch(() => {
          this.playing = false;
          this.emit('state-changed');
        });
      return;
    }

    this.scheduleCrossfade(true, index);
  }

  // ── Playlist / enabled-tracks API ────────────────────────────────────────

  /** Snapshot of currently-enabled indices, sorted ascending. */
  getEnabledIndices(): number[] {
    return Array.from(this.enabledSet).sort((a, b) => a - b);
  }

  isTrackEnabled(index: number): boolean {
    return this.enabledSet.has(index);
  }

  /**
   * Toggle a playlist entry on or off. Persists the new selection to
   * localStorage and emits `playlist-changed` so the UI can re-render.
   *
   * Safety rails:
   * - Refuses to disable the last enabled track (would leave playback
   *   with nothing to play).
   * - If the user disables the currently-audible track, transitions
   *   gracefully: crossfade to the next enabled track when audio is
   *   unmuted, silently reload the active element when muted so the
   *   next unmute picks up an enabled track instead of the one the user
   *   just removed.
   * - After any change, kicks `ensureAudiblePlayback()` so rapid
   *   toggling can't leave us in a state where audio is paused while
   *   the mute toggle is off. That "play arrows un-stuck the music"
   *   regression happens when the `this.playing` flag disagrees with
   *   the HTMLAudioElement's actual `paused` state (e.g. after a
   *   muting fade finished mid-crossfade); the safety net resolves it
   *   without requiring user input.
   */
  setTrackEnabled(index: number, enabled: boolean): void {
    if (index < 0 || index >= MUSIC_PLAYLIST.length) return;
    const was = this.enabledSet.has(index);
    if (was === enabled) return;
    if (enabled) {
      this.enabledSet.add(index);
    } else {
      if (this.enabledSet.size <= 1) return;
      this.enabledSet.delete(index);
    }
    this.persistEnabledSet();
    this.emit('playlist-changed');

    if (!enabled && this.currentIndex === index) {
      const target = this.findEnabledNeighbour(index, 'forward');
      if (target !== null && target !== index) {
        // Branch on the persisted mute state rather than
        // `this.playing` — the internal flag can be stale during mute
        // fade-outs or if a previous `play()` rejection left it
        // pointing the wrong way. The persisted flag is the user's
        // actual intent.
        if (isMusicMuted()) {
          this.jumpSilent(target);
        } else {
          this.scheduleCrossfade(true, target);
        }
      }
    }
    // If the user narrowed the playlist to a single track (or re-
    // broadened it), flip `activeEl.loop` so the lone track seamlessly
    // repeats instead of the player going silent when it ends.
    this.syncActiveLoop();
    this.ensureAudiblePlayback();
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private emit(event: PlayerEventName) {
    this.listeners[event].forEach((fn) => {
      try {
        fn();
      } catch {
        // Listener errors should never break playback.
      }
    });
  }

  private makeElement(src: string): HTMLAudioElement {
    const el = new Audio(src);
    el.preload = 'auto';
    el.loop = false;
    el.crossOrigin = 'anonymous';
    // Tag so dev-tools users know where this came from if it pops up in
    // the elements tree (for debugging — they're never in the DOM).
    el.dataset.role = 'lobby-music';
    return el;
  }

  private ensureTick() {
    if (this.tickTimer != null) return;
    this.tickTimer = setInterval(() => this.tick(), TICK_MS);
  }

  private stopTick() {
    if (this.tickTimer != null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private tick() {
    if (!this.activeEl || !this.playing) return;
    const el = this.activeEl;
    const now = Date.now();
    if (now - this.lastPlaybackPersistAt >= 3500) {
      this.persistPlaybackCursor();
    }
    const remaining = (el.duration || 0) - el.currentTime;
    if (!Number.isFinite(remaining) || remaining <= 0) return;
    if (!this.crossfadeScheduled && remaining <= FADE_SECONDS + 0.15) {
      // Auto-advance respects the enabled-tracks selection: when the
      // current track is about to end, roll forward to the next enabled
      // one so disabled tracks are skipped without audibly cutting in.
      // If the user has left only this track enabled the crossfade
      // would loop straight back into itself — let playback finish
      // naturally in that case so the element gets a chance to
      // seamless-loop rather than double-play.
      const target = this.findEnabledNeighbour(this.currentIndex, 'forward');
      if (target === null || target === this.currentIndex) return;
      this.scheduleCrossfade(false, target);
    }
  }

  // ── Enabled-tracks helpers ──────────────────────────────────────────────

  private firstEnabledIndex(): number {
    if (this.enabledSet.size === 0) return 0;
    let best = Number.POSITIVE_INFINITY;
    this.enabledSet.forEach((i) => {
      if (i < best) best = i;
    });
    return Number.isFinite(best) ? (best as number) : 0;
  }

  /**
   * Walk the playlist from `from` in the given direction and return the
   * first enabled index (including `from`'s neighbours but excluding
   * `from` itself). Returns `null` if no track is enabled. Used by
   * `next() / previous() / setTrackEnabled()` to keep navigation logic
   * in one spot.
   */
  private findEnabledNeighbour(
    from: number,
    direction: 'forward' | 'backward',
  ): number | null {
    if (this.enabledSet.size === 0) return null;
    const len = MUSIC_PLAYLIST.length;
    for (let step = 1; step <= len; step++) {
      const idx =
        direction === 'forward'
          ? (from + step) % len
          : (from - step + len) % len;
      if (this.enabledSet.has(idx)) return idx;
    }
    return null;
  }

  /**
   * Swap the active element to a new track without audible playback —
   * used when the user disables the current track while muted / pre-
   * gesture. Disposes the old element and mints a fresh one with the
   * new source rather than re-`src`-ing the existing element in place:
   * Safari in particular is known to reject a subsequent `play()` on
   * an element whose `src` was mid-flight changed (AbortError: "The
   * operation was aborted"), which was the root cause of the "music
   * stays silent until I hit next" regression we kept seeing after
   * rapid playlist toggles.
   */
  private jumpSilent(newIndex: number): void {
    if (newIndex === this.currentIndex) return;
    if (this.activeEl) {
      try {
        this.activeEl.pause();
        this.activeEl.src = '';
        this.activeEl.removeAttribute('src');
        this.activeEl.load?.();
      } catch {
        // Disposal is best-effort; the replacement below is what
        // actually matters.
      }
    }
    this.activeEl = this.makeElement(MUSIC_PLAYLIST[newIndex].src);
    this.activeEl.volume = 0;
    this.currentIndex = newIndex;
    this.syncActiveLoop();
    this.emit('track-changed');
  }

  /**
   * Self-healing step that runs after any user-initiated state change
   * (playlist toggle, for now — the same approach could be extended to
   * the mute handler). If the persisted mute flag is off but the
   * active element is paused, we try to resume playback: this covers
   * the "music stops responding after fiddling with the playlist"
   * class of races where `this.playing` fell out of sync with the
   * HTMLAudioElement's real state.
   *
   * Bails early in three cases so it doesn't step on other paths:
   * 1. `isMusicMuted()` — user wants silence, don't fight that.
   * 2. `crossfadeScheduled` — the crossfade already has its own play()
   *    kick and volumes are mid-ramp; touching the element now would
   *    introduce an audible glitch.
   * 3. `!activeEl.paused` — already playing; just align internal flag
   *    and ensure the tick pump is running.
   */
  private ensureAudiblePlayback(): void {
    if (typeof window === 'undefined') return;
    if (!this.activeEl) return;
    if (isMusicMuted()) return;
    if (this.crossfadeScheduled) return;
    const el = this.activeEl;
    if (!el.paused) {
      if (!this.playing) {
        this.playing = true;
        this.ensureTick();
        this.emit('state-changed');
      }
      return;
    }
    el.play()
      .then(() => {
        // Another state change could have swapped the active element
        // (e.g. a crossfade completed) while our play() was in flight —
        // in that case the element we kicked is now stale and shouldn't
        // drive the playing flag.
        if (this.activeEl !== el) return;
        if (isMusicMuted()) {
          el.pause();
          return;
        }
        this.playing = true;
        // Ramp up from wherever the element's volume currently is
        // (usually 0 after a fresh jumpSilent) rather than snapping
        // straight to target.
        this.fadeTo(el, TARGET_VOLUME, 0.6);
        this.ensureTick();
        this.emit('state-changed');
      })
      .catch(() => {
        // Autoplay is gated on a user gesture. The MusicController's
        // global pointerdown listener will retry on the next click.
      });
  }

  /**
   * Keep `activeEl.loop` in sync with the enabled-tracks selection. When
   * the playlist narrows to a single enabled track the crossfade
   * machinery refuses to fade a track into itself (that'd be an audible
   * double-play), so we let the audio element seamless-loop instead.
   * When the playlist broadens again we clear the flag so the usual
   * crossfade-at-end flow takes over.
   */
  private syncActiveLoop(): void {
    if (!this.activeEl) return;
    this.activeEl.loop =
      this.enabledSet.size === 1 && this.enabledSet.has(this.currentIndex);
  }

  private restoreEnabledSet(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(PLAYLIST_ENABLED_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return;
      const valid = parsed.filter(
        (n): n is number =>
          Number.isInteger(n) && (n as number) >= 0 && (n as number) < MUSIC_PLAYLIST.length,
      );
      // Treat an empty / all-disabled persisted set as corrupt — keep
      // the default "everything enabled" rather than booting into
      // silence.
      if (valid.length === 0) return;
      this.enabledSet = new Set(valid);
    } catch {
      // Ignore parse errors; user will just see the default selection.
    }
  }

  private persistEnabledSet(): void {
    if (typeof window === 'undefined') return;
    try {
      const arr = Array.from(this.enabledSet).sort((a, b) => a - b);
      window.localStorage.setItem(PLAYLIST_ENABLED_KEY, JSON.stringify(arr));
    } catch {
      // Storage quota / private mode — the selection just won't persist.
    }
  }

  private restorePlaybackCursor(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(PLAYBACK_CURSOR_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as { trackIndex?: unknown; timeSeconds?: unknown };
      const idx =
        typeof p.trackIndex === 'number' && Number.isInteger(p.trackIndex) ? p.trackIndex : -1;
      if (idx < 0 || idx >= MUSIC_PLAYLIST.length) return;
      if (!this.enabledSet.has(idx)) return;
      const t =
        typeof p.timeSeconds === 'number' && Number.isFinite(p.timeSeconds) && p.timeSeconds >= 0
          ? p.timeSeconds
          : 0;
      this.currentIndex = idx;
      this.resumeOffsetSec = t;
    } catch {
      // ignore corrupt storage
    }
  }

  /**
   * Seek after reload to the saved offset (best effort — needs duration metadata).
   */
  private applyResumeSeekIfNeeded(el: HTMLAudioElement): void {
    if (this.resumeOffsetSec <= 0) return;
    const seek = this.resumeOffsetSec;
    this.resumeOffsetSec = 0;
    const apply = () => {
      try {
        const d = el.duration;
        if (Number.isFinite(d) && d > 0.5) {
          el.currentTime = Math.min(seek, Math.max(0, d - 0.35));
        }
      } catch {
        // seek failed — stay at 0
      }
    };
    if (el.readyState >= HTMLMediaElement.HAVE_METADATA) apply();
    else el.addEventListener('loadedmetadata', apply, { once: true });
  }

  /**
   * Persist current track index + playback time for the next session.
   * Safe to call from `pagehide` / `beforeunload`.
   */
  persistPlaybackCursor(): void {
    if (typeof window === 'undefined') return;
    if (!this.activeEl || !this.hasLeadership) return;
    try {
      const t = this.activeEl.currentTime;
      if (!Number.isFinite(t) || t < 0) return;
      window.localStorage.setItem(
        PLAYBACK_CURSOR_KEY,
        JSON.stringify({ trackIndex: this.currentIndex, timeSeconds: t }),
      );
      this.lastPlaybackPersistAt = Date.now();
    } catch {
      // quota / private mode
    }
  }

  /**
   * Prepare the next element, start it at volume 0, and ramp both gains
   * in opposite directions. `forceNow` truncates the current track
   * immediately and is used by `next()` / `previous()`. When an explicit
   * `targetIndex` is passed we crossfade to that track instead of
   * stepping forward — lets `previous()` jump backwards without
   * duplicating this routine.
   */
  private scheduleCrossfade(forceNow: boolean, targetIndex?: number) {
    if (this.crossfadeScheduled) return;
    this.crossfadeScheduled = true;

    const nextIndex =
      typeof targetIndex === 'number'
        ? ((targetIndex % MUSIC_PLAYLIST.length) + MUSIC_PLAYLIST.length) %
          MUSIC_PLAYLIST.length
        : (this.currentIndex + 1) % MUSIC_PLAYLIST.length;
    const nextTrack = MUSIC_PLAYLIST[nextIndex];

    const nextEl = this.makeElement(nextTrack.src);
    nextEl.volume = 0;
    this.nextEl = nextEl;

    const startNext = () => {
      nextEl.play().catch(() => {
        // If the next track blocks, clean up and let the current one
        // ride out naturally — we'll retry on the next gesture.
        this.crossfadeScheduled = false;
        this.nextEl = null;
      });
    };

    // Start the next element ASAP so its buffer is warm before the fade
    // starts moving. Most browsers start a few hundred ms of ahead-load
    // quickly from a local fetch.
    startNext();

    const fadeMs = forceNow ? 1200 : FADE_SECONDS * 1000;
    const currentEl = this.activeEl;

    this.fadeEndsAt = performance.now() + fadeMs;
    this.beginCrossfade(currentEl, nextEl, fadeMs, () => {
      // Swap roles. The previous "active" element is disposed so we
      // don't leak audio decoders across the 9-track cycle.
      if (currentEl) {
        currentEl.pause();
        currentEl.src = '';
        currentEl.removeAttribute('src');
        currentEl.load?.();
      }
      this.activeEl = nextEl;
      this.nextEl = null;
      this.currentIndex = nextIndex;
      this.crossfadeScheduled = false;
      this.syncActiveLoop();
      this.emit('track-changed');
      this.persistPlaybackCursor();
    });
  }

  /** Linear volume ramp on two elements over `durationMs`. */
  private beginCrossfade(
    from: HTMLAudioElement | null,
    to: HTMLAudioElement,
    durationMs: number,
    onDone: () => void,
  ) {
    if (this.fadeTimer != null) {
      clearInterval(this.fadeTimer);
      this.fadeTimer = null;
    }
    const fromStart = from?.volume ?? 0;
    const toTarget = TARGET_VOLUME;
    const startedAt = performance.now();

    const step = () => {
      const t = Math.min(1, (performance.now() - startedAt) / durationMs);
      if (from) from.volume = Math.max(0, fromStart * (1 - t));
      to.volume = Math.min(toTarget, toTarget * t);
      if (t >= 1) {
        if (this.fadeTimer != null) {
          clearInterval(this.fadeTimer);
          this.fadeTimer = null;
        }
        onDone();
      }
    };
    this.fadeTimer = setInterval(step, TICK_MS);
  }

  /** Linear volume ramp on a single element (for mute / first-play). */
  private fadeTo(
    el: HTMLAudioElement,
    target: number,
    durationSec: number,
    onDone?: () => void,
  ) {
    const startVol = el.volume;
    const startedAt = performance.now();
    const durationMs = durationSec * 1000;
    let timer: ReturnType<typeof setInterval> | null = null;

    const step = () => {
      const t = Math.min(1, (performance.now() - startedAt) / durationMs);
      el.volume = startVol + (target - startVol) * t;
      if (t >= 1) {
        if (timer != null) clearInterval(timer);
        onDone?.();
      }
    };
    timer = setInterval(step, TICK_MS);
  }
}

// ── Singleton ────────────────────────────────────────────────────────────────

let instance: MusicPlayer | null = null;

export function getMusicPlayer(): MusicPlayer {
  if (!instance) instance = new MusicPlayer();
  return instance;
}
